import React, { useState } from 'react';
import { save_new_participant, video_participant_delete, video_room_participants } from '../../../../Services/Service';
import styles from './ParticipantsModal.module.css';

const ParticipantsModal = ({ participants, onClose, onUpdate, loading = false, roomId }) => {
  const [participantsList, setParticipantsList] = useState(participants);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [deletingParticipant, setDeletingParticipant] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [fetchingParticipants, setFetchingParticipants] = useState(false); // Add this

  const filteredParticipants = participants.filter(participant =>
    participant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (participant.mobile||participant.mobileNo).includes(searchTerm)
  );

  // Clear validation message function
  const clearValidationMessage = () => {
    setValidationMessage('');
    setMessageType('');
  };

  // Show validation message function
  const showValidationMessage = (message, type = 'error') => {
    setValidationMessage(message);
    setMessageType(type);
    // Auto clear after 5 seconds
    setTimeout(() => {
      clearValidationMessage();
    }, 5000);
  };

  // Fetch fresh participants data from API
  const fetchParticipants = async () => {
    try {
      setFetchingParticipants(true);
      const response = await video_room_participants(roomId);
      
      if (response.data.code === 200) {
        const apiParticipants = response.data.data.videoCallParticipants || [];
        setParticipantsList(apiParticipants);
        onUpdate(apiParticipants); // Update parent component with fresh data
      } else {
        console.error('Failed to fetch participants:', response);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setFetchingParticipants(false);
    }
  };

  const handleDelete = async (participant) => {
    if (!participant.id || !roomId) {
      console.error('Missing participant ID or room ID');
      return;
    }

    if (participant.type === 'host') {
      showValidationMessage('Cannot delete the host participant', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${participant.name} from this session?`)) {
      return;
    }

    try {
      setDeletingParticipant(participant.id);
      clearValidationMessage();
      
      const response = await video_participant_delete(participant.id, roomId);
      
      if (response.data.code === 200) {
        showValidationMessage('Participant removed successfully', 'success');
        // Fetch fresh data instead of manually updating
        await fetchParticipants();
      } else {
        console.error('Failed to delete participant:', response);
        showValidationMessage('Failed to remove participant. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting participant:', error);
      showValidationMessage('Failed to remove participant. Please try again.', 'error');
    } finally {
      setDeletingParticipant(null);
    }
  };

  const validateMobileNumber = (mobile) => {
    // Basic mobile number validation
    const mobileRegex = /^[+]?[\d\s\-()]{10,15}$/;
    return mobileRegex.test(mobile.trim());
  };

 const handleAdd = async () => {
  clearValidationMessage();
  
  if (!newMobile.trim()) {
    showValidationMessage('Please enter a mobile number', 'warning');
    return;
  }

  if (!validateMobileNumber(newMobile)) {
    showValidationMessage('Please enter a valid mobile number', 'warning');
    return;
  }

  if (!roomId) {
    console.error('Missing room ID');
    showValidationMessage('Session ID is missing. Cannot add participant.', 'error');
    return;
  }

  // Check if participant already exists - with proper null checks
  const existingParticipant = participantsList.find(p => {
    // Check if participant and mobile exist
    if (!p || !p.mobile || typeof p.mobile !== 'string') {
      return false;
    }
    
    const existingMobile = p.mobile.replace(/\s|-|\(|\)/g, '');
    const newMobileClean = newMobile.replace(/\s|-|\(|\)/g, '');
    
    return existingMobile === newMobileClean;
  });
  
  if (existingParticipant) {
    showValidationMessage('This participant is already added to the session', 'warning');
    return;
  }

  try {
    setAddingParticipant(true);
    
    const response = await save_new_participant(roomId, newMobile.trim());
    console.log('API Response:', response.data);
    
    if (response.data.code === 200) {
      const responseData = response.data;
      
      // Check for API-level errors
      if (responseData.notRegistered && responseData.notRegistered.length > 0) {
        showValidationMessage(`Mobile number ${responseData.notRegistered[0]} is not registered in the system`, 'error');
        return;
      }
      
      if (responseData.alreadyExist && responseData.alreadyExist.length > 0) {
        showValidationMessage(`Mobile number ${responseData.alreadyExist[0]} already exists in this session`, 'warning');
        return;
      }
      
      // If we reach here, participant was added successfully
      setNewMobile('');
      showValidationMessage('Participant added successfully', 'success');
      
      // Fetch fresh data instead of manually updating
      await fetchParticipants();
    } else {
      console.error('Failed to add participant:', response);
      showValidationMessage('Failed to add participant. Please try again.', 'error');
    }
  } catch (error) {
    console.error('Error adding participant:', error);
    showValidationMessage('Failed to add participant. Please try again.', 'error');
  } finally {
    setAddingParticipant(false);
  }
};

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  // Clear validation message when input changes
  const handleMobileChange = (e) => {
    setNewMobile(e.target.value);
    if (validationMessage) {
      clearValidationMessage();
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2>Participants List</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading participants...</p>
          </div>
        ) : (
          <>
            <div className={styles.searchContainer}>
              <label>Search:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search participants..."
                className={styles.searchInput}
              />
            </div>

            <div className={styles.tableContainer}>
              {fetchingParticipants && (
                <div className={styles.tableLoadingOverlay}>
                  <div className={styles.smallSpinner}></div>
                  <span>Updating participants...</span>
                </div>
              )}
              
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Participant Name</th>
                    <th>Mobile Number</th>
                    <th>Participant Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipants.length === 0 ? (
                    <tr>
                      <td colSpan="4" className={styles.noData}>
                        {searchTerm ? 'No participants found matching your search' : 'No participants found'}
                      </td>
                    </tr>
                  ) : (
                    filteredParticipants.map((participant) => (
                      <tr key={participant.id}>
                        <td>{participant.name}</td>
                        <td>{participant.mobile || participant.mobileNo}</td>
                        <td>
                          {
                            participant.userId == 0 ?
                                <span className={`${styles.typeBadge}`} style={{ width: 60, backgroundColor: 'rgb(238 207 204)', color: '#F44336' }}>
                                    Patient not registered
                                </span>
                                :
                                participant.type == '' ?
                                    <span className={`${styles.typeBadge}`} style={{ width: 60, backgroundColor: 'rgb(190 190 190 / 36%)', color: 'rgb(130 130 130)' }}>
                                        {participant.type == '' && 'Invited'} 
                                    </span> :
                                    <span className={`${styles.typeBadge} ${styles[participant.type]} `}>
                                        {participant.type}
                                    </span>
                        }
                          {/* {
                            participant.type=='' ?                            
                          <span className={`${styles.typeBadge}`} style={{width:60,backgroundColor:'rgb(190 190 190 / 36%)',color: 'rgb(130 130 130)'}}>
                            {participant.type==''&&'Invited'}
                          </span>:
                          <span className={`${styles.typeBadge} ${styles[participant.type]} `}>
                            {participant.type}
                          </span>
                          } */}
                        </td>
                        <td>
                          {participant.type === 'host' ? (
                            <button
                              className={`${styles.actionBtn} ${styles.disabled}`}
                              disabled
                              title="Cannot delete host"
                            >
                              <i className="bi bi-shield-check"></i>
                            </button>
                          ) : (
                            <button
                              className={styles.actionBtn}
                              onClick={() => handleDelete(participant)}
                              disabled={deletingParticipant === participant.id || fetchingParticipants}
                              title="Remove participant"
                            >
                              {deletingParticipant === participant.id ? (
                                <div className={styles.smallSpinner}></div>
                              ) : (
                                <i className="bi bi-trash"></i>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.addSection}>
              <p>Showing {filteredParticipants.length} of {participantsList.length} entries</p>
              
              {/* Validation Message */}
              {validationMessage && (
                <div className={`${styles.validationMessage} ${styles[messageType]}`}>
                  <span className={styles.messageIcon}>
                    {messageType === 'success' && '✓'}
                    {messageType === 'error' && '⚠️'}
                    {messageType === 'warning' && '⚠️'}
                  </span>
                  <span>{validationMessage}</span>
                  <button 
                    className={styles.closeMessage}
                    onClick={clearValidationMessage}
                  >
                    ×
                  </button>
                </div>
              )}
              
              <div className={styles.addContainer}>
                <label>Enter Mobile No:</label>
                <input
                  type="tel"
                  value={newMobile}
                  onChange={handleMobileChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter mobile number (e.g., 9876543210)"
                  className={styles.addInput}
                  disabled={addingParticipant || fetchingParticipants}
                />
                <button 
                  className={styles.addBtn} 
                  onClick={handleAdd}
                  disabled={addingParticipant || !newMobile.trim() || fetchingParticipants}
                >
                  {addingParticipant ? (
                    <>
                      <div className={styles.smallSpinner}></div>
                      Adding...
                    </>
                  ) : (
                    'Add'
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ParticipantsModal;